import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowDown, ArrowUp, Copy, MagnifyingGlass, PencilSimple, Plus, Spinner, Trash, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { QuestionEditor } from "./QuestionBankPage";
import { ExamStyleEditorDialog } from "../components/ExamStyleManagementBlock";

const STATUS_LABELS = { draft: "Rascunho", ready: "Pronta", reviewed: "Revisada", needs_review: "Aguardando revisão", retired: "Aposentada" };
const todayInputValue = () => {
  const value = new Date();
  value.setMinutes(value.getMinutes() - value.getTimezoneOffset());
  return value.toISOString().slice(0, 10);
};

const editorialAuthor = (question) => (
  question?.content_author_name || question?.author_name || question?.content_source_label || "Origem editorial não informada"
);

function QuestionRow({ question, selected, onToggle, onEdit, onDuplicate }) {
  const stats = question.stats || {};
  const retired = question.status === "retired";
  const unavailable = retired || question.auto_selection_eligible === false;
  const unavailableTitle = retired
    ? "Questão aposentada"
    : "Questão em revisão ou excluída do sorteio; indisponível para uma nova prova";
  return (
    <article className={`border p-4 ${selected ? "border-primary bg-primary/5" : "border-border bg-card"}`}>
      <div className="flex gap-3">
        <input
          type="checkbox"
          className="mt-1"
          checked={selected}
          disabled={unavailable}
          onChange={() => onToggle(question)}
          title={unavailable ? unavailableTitle : "Adicionar à prova"}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap gap-2 items-center mb-2">
            <span className="font-mono-data font-bold text-primary">{question.code}</span>
            <span className="border px-2 py-0.5 text-[10px] uppercase">{STATUS_LABELS[question.status] || question.status}</span>
            <span className="border px-2 py-0.5 text-[10px] uppercase">{question.visibility === "public" ? "Pública" : "Privada"}</span>
            <span className="border px-2 py-0.5 text-[10px]">v{question.version}</span>
            {question.priority_in_auto_selection && <span className="border border-primary bg-primary/5 text-primary px-2 py-0.5 text-[10px]">prioridade 4×</span>}
            {question.auto_selection_eligible === false && !retired && <span className="border border-destructive bg-destructive/5 text-destructive px-2 py-0.5 text-[10px]">em revisão / não reutilizar</span>}
          </div>
          <div className="font-semibold leading-relaxed line-clamp-3">{question.stem}</div>
          <div className="text-xs text-muted-foreground mt-2">
            {question.knowledge_area} · {question.system_context}
          </div>
          <div className="text-xs mt-1" data-testid="question-editorial-author">
            <b>Autor editorial / banca:</b> {editorialAuthor(question)}
            {question.content_attribution_status === "needs_review" ? <span className="text-amber-700"> · origem a confirmar</span> : null}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">Este é o crédito impresso após a questão no caderno da prova.</div>
          {question.source_reference && <div className="text-xs text-muted-foreground mt-1"><b>Fonte:</b> {question.source_reference}</div>}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-xs">
            <div><span className="text-muted-foreground">Aplicações:</span> {stats.exam_count || 0}</div>
            <div><span className="text-muted-foreground">Acerto:</span> {stats.average_correct_percentage == null ? "—" : `${stats.average_correct_percentage}%`}</div>
            <div><span className="text-muted-foreground">Discriminação:</span> {stats.average_point_biserial ?? "—"}</div>
            <div><span className="text-muted-foreground">Qualidade:</span> {stats.quality || "—"}</div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {question.can_edit ? (
              <Button type="button" size="sm" variant="outline" className="rounded-none" onClick={() => onEdit(question)}>
                <PencilSimple className="mr-2" />Editar
              </Button>
            ) : (
              <Button type="button" size="sm" variant="outline" className="rounded-none" onClick={() => onDuplicate(question)}>
                <Copy className="mr-2" />Duplicar para editar
              </Button>
            )}
            <Button type="button" size="sm" variant={selected ? "default" : "outline"} className="rounded-none" disabled={unavailable} onClick={() => onToggle(question)}>
              {selected ? "Remover da prova" : "Adicionar à prova"}
            </Button>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function ExamQuestionBuilder({ open, onOpenChange, schools = [], classes = [], semesters = [], stages = [], disciplines = [], layouts = [], creationContext = null, onCreated }) {
  const [taxonomy, setTaxonomy] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState([]);
  const [name, setName] = useState("");
  const [examDate, setExamDate] = useState(todayInputValue());
  const [classIds, setClassIds] = useState([]);
  const [schoolScopeId, setSchoolScopeId] = useState("");
  const [schoolWide, setSchoolWide] = useState(false);
  const [allowExceptionalClasses, setAllowExceptionalClasses] = useState(false);
  const [semesterId, setSemesterId] = useState("");
  const [stageId, setStageId] = useState("");
  const [academicScope, setAcademicScope] = useState("global");
  const [disciplineId, setDisciplineId] = useState("");
  const [perceptionCount, setPerceptionCount] = useState(9);
  const [layoutId, setLayoutId] = useState("saki-oficial-a4-100");
  const [deliveryMode, setDeliveryMode] = useState("hybrid");
  const [randomize, setRandomize] = useState(true);
  const [filters, setFilters] = useState({ scope: "all", q: "", knowledge_area: "all", system_context: "all" });
  const [examStyles, setExamStyles] = useState([]);
  const [styleId, setStyleId] = useState("manual");
  const [questionCount, setQuestionCount] = useState(100);
  const [composing, setComposing] = useState(false);
  const [composition, setComposition] = useState(null);
  const [styleEditorOpen, setStyleEditorOpen] = useState(false);
  const [compositionFilters, setCompositionFilters] = useState({
    expected_difficulty: "all",
    observed_correct_min: "",
    observed_correct_max: "",
    min_discrimination: "",
    include_unmeasured: true,
  });

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setName("");
    setExamDate(todayInputValue());
    setClassIds([]);
    const assignments = creationContext?.teacher_assignments || [];
    const teacherOnly = Boolean(creationContext && !creationContext.is_admin && !creationContext.can_create_global);
    const teacherSchoolIds = [...new Set(assignments.map((item) => item.school_id).filter(Boolean))];
    const startingSchoolId = teacherOnly && teacherSchoolIds.length === 1 ? teacherSchoolIds[0] : "";
    const currentSemester = semesters.find((item) => item.is_current && assignments.some((assignment) => (
      assignment.semester_id === item.id && (!startingSchoolId || assignment.school_id === startingSchoolId)
    )));
    const offeredSemester = assignments.find((item) => !startingSchoolId || item.school_id === startingSchoolId)?.semester_id;
    setSchoolScopeId(startingSchoolId);
    setSchoolWide(false);
    setAllowExceptionalClasses(false);
    setSemesterId(currentSemester?.id || offeredSemester || semesters.find((item) => item.is_current)?.id || "");
    setStageId("");
    setAcademicScope("global");
    setDisciplineId("");
    setPerceptionCount(9);
    setLayoutId(layouts.find((item) => item.is_default)?.id || "saki-oficial-a4-100");
    setDeliveryMode("hybrid");
    setRandomize(true);
    setFilters({ scope: "all", q: "", knowledge_area: "all", system_context: "all" });
    setStyleId("manual");
    setQuestionCount(Number(layouts.find((item) => item.is_default)?.default_question_count || 100));
    setComposition(null);
    setCompositionFilters({ expected_difficulty: "all", observed_correct_min: "", observed_correct_max: "", min_discrimination: "", include_unmeasured: true });
    api.get("/questions/taxonomy")
      .then((response) => setTaxonomy(response.data))
      .catch((error) => toast.error(fmtError(error)));
  }, [open, layouts, semesters, creationContext]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => {
      api.get(`/exam-styles?question_count=${Math.max(1, Math.min(200, Number(questionCount || 1)))}`)
        .then((response) => setExamStyles(response.data.items || []))
        .catch((error) => toast.error(fmtError(error)));
    }, 180);
    return () => clearTimeout(timer);
  }, [open, questionCount]);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page_size: "100" });
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== "all") params.set(key, value);
      });
      const response = await api.get(`/questions?${params}`);
      setQuestions((response.data.items || []).filter((item) => item.status !== "retired"));
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setLoading(false);
    }
  }, [filters, open]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const selectedIds = useMemo(() => new Set(selected.map((item) => item.id)), [selected]);
  const selectedEditorialSummary = useMemo(() => {
    const counts = new Map();
    selected.forEach((question) => {
      const author = editorialAuthor(question);
      counts.set(author, (counts.get(author) || 0) + 1);
    });
    return [...counts.entries()].sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0], "pt-BR"));
  }, [selected]);
  const activeLayout = layouts.find((item) => item.id === layoutId)
    || layouts.find((item) => item.is_default)
    || { max_question_count: 100, max_perception_count: 10 };
  const onlineOnly = deliveryMode === "online_only";
  const effectiveQuestionMax = onlineOnly ? 200 : activeLayout.max_question_count;
  const activeStyle = examStyles.find((item) => item.id === styleId);
  const selectedClassSchoolIds = Array.from(new Set(
    classIds.map((id) => classes.find((item) => item.id === id)?.school_id).filter(Boolean),
  ));
  const selectedSchoolId = schoolScopeId || (selectedClassSchoolIds.length === 1 ? selectedClassSchoolIds[0] : "");
  const selectableSchools = (creationContext?.is_admin || !creationContext)
    ? schools
    : schools.filter((item) => (
      (creationContext.school_manager_school_ids || []).includes(item.id)
      || (creationContext.stage_manager_assignments || []).some((entry) => entry.school_id === item.id)
      || (creationContext.teacher_assignments || []).some((entry) => entry.school_id === item.id)
    ));
  const teacherAssignments = creationContext?.teacher_assignments || [];
  const onlyTeacherAssignments = Boolean(creationContext && !creationContext.is_admin && !creationContext.can_create_global);
  const teacherAssignmentCandidates = teacherAssignments.filter((entry) => (
    entry.semester_id === semesterId
    && (!entry.school_id || entry.school_id === selectedSchoolId)
    && (!stageId || entry.stage_id === stageId)
  ));
  const teacherAssignmentForExam = disciplineId
    ? teacherAssignmentCandidates.find((entry) => entry.discipline_id === disciplineId)
    : (teacherAssignmentCandidates.length === 1 ? teacherAssignmentCandidates[0] : null);
  const teacherDefaultClassIds = (teacherAssignmentForExam?.class_ids || []).filter((id) => classes.some((item) => item.id === id));
  const teacherStageIds = new Set(teacherAssignments
    .filter((entry) => entry.semester_id === semesterId && (!entry.school_id || entry.school_id === selectedSchoolId))
    .map((entry) => entry.stage_id)
    .filter(Boolean));
  const teacherStageClassIds = new Set(teacherAssignments
    .filter((entry) => entry.semester_id === semesterId
      && (!entry.school_id || entry.school_id === selectedSchoolId)
      && (!stageId || entry.stage_id === stageId))
    .flatMap((entry) => entry.class_ids || []));
  const availableSemesters = semesters.filter((item) => !item.school_id || !selectedSchoolId || item.school_id === selectedSchoolId);
  const availableStages = stages.filter((item) => (item.matrix_id || item.semester_id === semesterId)
    && (!selectedSchoolId || item.school_id === selectedSchoolId)
    && (!onlyTeacherAssignments || teacherStageIds.has(item.id))
    && (onlyTeacherAssignments || !classIds.length || classIds.every((id) => (item.class_ids || []).includes(id))));
  const availableDisciplines = disciplines.filter((item) => item.stage_id === stageId
    && (!onlyTeacherAssignments || teacherAssignments.some((entry) => (
      entry.discipline_id === item.id && entry.semester_id === semesterId
      && (!entry.school_id || entry.school_id === selectedSchoolId)
    ))));
  const canCreateGlobal = Boolean(creationContext?.is_admin || creationContext?.can_create_global);
  const canCreateSchoolWide = Boolean(creationContext?.is_admin || creationContext?.can_create_school_wide);
  const availableClasses = classes.filter((item) => (
    (!selectedSchoolId || item.school_id === selectedSchoolId)
    && (!onlyTeacherAssignments || !stageId || !teacherStageClassIds.size || teacherStageClassIds.has(item.id))
    && (!onlyTeacherAssignments || allowExceptionalClasses || !disciplineId || teacherDefaultClassIds.includes(item.id))
  ));
  const schoolName = (id) => schools.find((item) => item.id === id)?.name || "";

  useEffect(() => {
    if (!onlyTeacherAssignments || schoolScopeId) return;
    const teacherSchoolIds = [...new Set(teacherAssignments.map((entry) => entry.school_id).filter(Boolean))];
    if (teacherSchoolIds.length === 1) setSchoolScopeId(teacherSchoolIds[0]);
  }, [onlyTeacherAssignments, schoolScopeId, teacherAssignments]);

  useEffect(() => {
    if (!onlyTeacherAssignments || !selectedSchoolId) return;
    const offeredSemesterIds = [...new Set(teacherAssignments
      .filter((entry) => !entry.school_id || entry.school_id === selectedSchoolId)
      .map((entry) => entry.semester_id)
      .filter(Boolean))];
    if (!offeredSemesterIds.length || offeredSemesterIds.includes(semesterId)) return;
    const currentOffered = semesters.find((item) => item.is_current && offeredSemesterIds.includes(item.id));
    setSemesterId(currentOffered?.id || offeredSemesterIds[0]);
  }, [onlyTeacherAssignments, selectedSchoolId, semesterId, teacherAssignments, semesters]);

  useEffect(() => {
    if (semesterId && !availableSemesters.some((item) => item.id === semesterId)) {
      setSemesterId(""); setStageId(""); setDisciplineId("");
    }
  }, [selectedSchoolId, semesterId, semesters]);

  useEffect(() => {
    if (stageId && !availableStages.some((item) => item.id === stageId)) {
      setStageId(""); setDisciplineId("");
    }
  }, [semesterId, stageId, classIds, stages]);

  useEffect(() => {
    if (!onlyTeacherAssignments || !semesterId || !selectedSchoolId || stageId || availableStages.length !== 1) return;
    setStageId(availableStages[0].id);
    setAcademicScope("discipline");
  }, [onlyTeacherAssignments, semesterId, selectedSchoolId, stageId, availableStages]);

  useEffect(() => {
    if (!onlyTeacherAssignments || !stageId || disciplineId || availableDisciplines.length !== 1) return;
    setDisciplineId(availableDisciplines[0].id);
    setAcademicScope("discipline");
  }, [onlyTeacherAssignments, stageId, disciplineId, availableDisciplines]);

  useEffect(() => {
    if (!onlyTeacherAssignments || allowExceptionalClasses || !disciplineId || !teacherAssignmentForExam) return;
    setClassIds((current) => (
      current.length === teacherDefaultClassIds.length
      && current.every((id) => teacherDefaultClassIds.includes(id))
        ? current
        : teacherDefaultClassIds
    ));
  }, [onlyTeacherAssignments, allowExceptionalClasses, disciplineId, teacherAssignmentForExam, teacherDefaultClassIds]);

  useEffect(() => {
    if (!canCreateGlobal && academicScope !== "discipline") {
      setAcademicScope("discipline");
      setSchoolWide(false);
    }
  }, [canCreateGlobal, academicScope]);

  const toggle = (question) => {
    if (!selectedIds.has(question.id) && selected.length >= effectiveQuestionMax) {
      toast.error(onlineOnly
        ? "A aplicação online aceita até 200 questões"
        : `${activeLayout.name || "Este layout"} aceita até ${activeLayout.max_question_count} questões`);
      return;
    }
    setComposition(null);
    setSelected((current) => current.some((item) => item.id === question.id)
      ? current.filter((item) => item.id !== question.id)
      : [...current, question]);
  };

  const move = (index, direction) => {
    setComposition(null);
    setSelected((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const duplicate = async (question) => {
    try {
      const response = await api.post(`/questions/${question.id}/duplicate`);
      toast.success("Cópia privada criada. Edite antes de usar, se necessário.");
      await load();
      setEditing(response.data);
      setEditorOpen(true);
    } catch (error) {
      toast.error(fmtError(error));
    }
  };

  const handleSaved = async (saved) => {
    await load();
    setComposition(null);
    setSelected((current) => {
      const exists = current.some((item) => item.id === saved.id);
      if (exists) return current.map((item) => item.id === saved.id ? { ...item, ...saved } : item);
      return [...current, saved];
    });
  };

  const composeSelection = async () => {
    if (styleId === "manual") return toast.error("Escolha um estilo automático");
    const target = Math.max(1, Math.min(effectiveQuestionMax, Number(questionCount || 0)));
    setComposing(true);
    try {
      const payload = {
        style_id: styleId,
        question_count: target,
        scope: filters.scope,
        q: filters.q.trim() || null,
        system_context: filters.system_context === "all" ? null : filters.system_context,
        expected_difficulty: compositionFilters.expected_difficulty === "all" ? null : compositionFilters.expected_difficulty,
        observed_correct_min: compositionFilters.observed_correct_min === "" ? null : Number(compositionFilters.observed_correct_min),
        observed_correct_max: compositionFilters.observed_correct_max === "" ? null : Number(compositionFilters.observed_correct_max),
        min_discrimination: compositionFilters.min_discrimination === "" ? null : Number(compositionFilters.min_discrimination),
        include_unmeasured: compositionFilters.include_unmeasured,
      };
      const response = await api.post("/questions/compose", payload);
      setSelected(response.data.questions || []);
      setComposition(response.data);
      if (response.data.complete) toast.success(`${response.data.selected_count} questões sorteadas com a composição prevista`);
      else toast.error(`Faltam ${response.data.requested - response.data.selected_count} questão(ões) para completar o estilo`);
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setComposing(false);
    }
  };

  const create = async () => {
    if (!name.trim()) return toast.error("Informe o nome da prova");
    if (!examDate) return toast.error("Informe a data de realização");
    if (!selectedSchoolId) return toast.error("Selecione a escola");
    if (!semesterId) return toast.error("Selecione o semestre");
    if ((onlyTeacherAssignments && !stageId) || (stageId && academicScope === "discipline" && !disciplineId)) return toast.error("Selecione a disciplina vinculada");
    if (!selected.length) return toast.error("Adicione ao menos uma questão");
    if (styleId !== "manual" && !composition?.complete) return toast.error("Execute novamente o sorteio para validar a composição escolhida");
    setSaving(true);
    try {
      const response = await api.post("/exams/from-questions", {
        name: name.trim(),
        exam_date: examDate,
        question_ids: selected.map((item) => item.id),
        class_ids: classIds,
        school_id: selectedSchoolId,
        school_wide: Boolean(academicScope === "global" && schoolWide),
        allow_exceptional_classes: Boolean(allowExceptionalClasses),
        semester_id: semesterId,
        stage_id: stageId || null,
        academic_scope: academicScope === "global" && schoolWide ? "global" : (stageId ? academicScope : "legacy"),
        discipline_id: stageId && academicScope === "discipline" ? disciplineId : null,
        perception_count: Number(perceptionCount || 0),
        layout_id: layoutId,
        delivery_mode: deliveryMode,
        randomize_options: randomize,
        composition_style_id: composition?.complete ? composition.style?.id : null,
        composition_seed: composition?.complete ? composition.seed : null,
        composition_filters: composition?.complete ? composition.filters : null,
      });
      toast.success("Prova criada com as questões selecionadas");
      onCreated?.(response.data);
      onOpenChange(false);
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="rounded-none max-w-[1500px] w-[96vw] h-[95dvh] max-h-[95dvh] min-h-0 content-start overflow-y-auto overscroll-y-contain touch-pan-y"
          style={{ WebkitOverflowScrolling: "touch" }}
          aria-describedby="exam-question-builder-desc"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-4xl font-black">Nova prova com questões</DialogTitle>
          </DialogHeader>
          <p id="exam-question-builder-desc" className="text-sm text-muted-foreground">
            Configure a prova, selecione itens do Banco de Questões e crie ou edite questões sem sair desta montagem. As permissões respeitam autoria e hierarquia.
          </p>

          <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-6 mt-4">
            <aside className="space-y-4">
              <section className="border border-border bg-card p-4 space-y-4">
                <div className="font-mono-data font-bold">Dados da prova</div>
                <Input className="rounded-none" placeholder="Nome da prova" value={name} onChange={(e) => setName(e.target.value)} />
                <div>
                  <div className="label-eyebrow mb-1">data de realização</div>
                  <Input type="date" className="rounded-none" value={examDate} onChange={(e) => setExamDate(e.target.value)} />
                </div>
                <div>
                  <div className="label-eyebrow mb-1">escola</div>
                  <Select value={selectedSchoolId} onValueChange={(value) => { setSchoolScopeId(value); setClassIds([]); setStageId(""); setDisciplineId(""); setAllowExceptionalClasses(false); }}>
                    <SelectTrigger className="rounded-none"><SelectValue placeholder="Selecione a escola" /></SelectTrigger>
                    <SelectContent>{selectableSchools.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="label-eyebrow mb-1">semestre</div>
                  <Select value={semesterId} onValueChange={setSemesterId}>
                    <SelectTrigger className="rounded-none"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>{availableSemesters.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}{item.school_id ? ` · ${schoolName(item.school_id)}` : " · legado/global"}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="label-eyebrow mb-1">etapa</div>
                  <Select value={stageId || "none"} onValueChange={(value) => { setStageId(value === "none" ? "" : value); setAcademicScope(onlyTeacherAssignments ? "discipline" : "global"); setDisciplineId(""); setClassIds([]); setAllowExceptionalClasses(false); }}>
                    <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>{!onlyTeacherAssignments && <SelectItem value="none">Sem etapa / legado</SelectItem>}{availableStages.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {stageId && (
                  <div>
                    <div className="label-eyebrow mb-1">tipo acadêmico</div>
                    <Select value={academicScope} onValueChange={(value) => { setAcademicScope(value); if (value === "global") setDisciplineId(""); }}>
                      <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>{canCreateGlobal && <SelectItem value="global">Global da etapa / simulado</SelectItem>}<SelectItem value="discipline">Prova de disciplina</SelectItem></SelectContent>
                    </Select>
                  </div>
                )}
                {stageId && academicScope === "discipline" && (
                  <div>
                    <div className="label-eyebrow mb-1">disciplina</div>
                    <Select value={disciplineId} onValueChange={setDisciplineId}>
                      <SelectTrigger className="rounded-none"><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>{availableDisciplines.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {onlyTeacherAssignments && stageId && academicScope === "discipline" && disciplineId && teacherAssignmentForExam && (
                  <label className="flex gap-3 border border-border bg-muted/30 p-3 text-sm">
                    <input
                      type="checkbox"
                      checked={allowExceptionalClasses}
                      onChange={(event) => {
                        const enabled = event.target.checked;
                        setAllowExceptionalClasses(enabled);
                        if (!enabled) setClassIds(teacherDefaultClassIds);
                      }}
                    />
                    <span><b>Escolher excepcionalmente outra turma</b><br /><span className="text-muted-foreground">As turmas da disciplina neste semestre já ficam selecionadas por padrão.</span></span>
                  </label>
                )}
                {academicScope === "global" && canCreateSchoolWide && (
                  <label className="flex gap-3 border border-primary bg-primary/5 p-3 text-sm">
                    <input type="checkbox" checked={schoolWide} onChange={(event) => { setSchoolWide(event.target.checked); if (event.target.checked) setClassIds([]); }} />
                    <span><b>Aplicar à escola toda</b><br /><span className="text-muted-foreground">Inclui todas as turmas e libera a análise por etapa e área.</span></span>
                  </label>
                )}
                <div>
                  <div className="label-eyebrow mb-2">turmas <span className="normal-case text-muted-foreground">{onlyTeacherAssignments && !allowExceptionalClasses ? "(da disciplina, pré-selecionadas)" : "(opcional)"}</span></div>
                  <div className="border border-border p-3 max-h-40 overflow-y-auto grid gap-2">
                    {availableClasses.map((item) => (
                      <label key={item.id} className="flex gap-2 text-sm cursor-pointer">
                        <input type="checkbox" disabled={schoolWide} checked={classIds.includes(item.id)} onChange={() => setClassIds((current) => current.includes(item.id) ? current.filter((id) => id !== item.id) : [...current, item.id])} />
                        {item.name}{item.school_id ? ` · ${schoolName(item.school_id)}` : ""}
                      </label>
                    ))}
                    {!availableClasses.length && <div className="text-xs text-muted-foreground">Selecione a disciplina para carregar as turmas configuradas.</div>}
                  </div>
                </div>
                <div>
                  <div className="label-eyebrow mb-1">forma de aplicação</div>
                  <Select value={deliveryMode} onValueChange={(value) => {
                    setDeliveryMode(value);
                    setComposition(null);
                    if (value === "online_only") {
                      setPerceptionCount(0);
                      setQuestionCount((current) => Math.min(200, Number(current || 100)));
                    } else {
                      setSelected((current) => current.slice(0, activeLayout.max_question_count));
                      setQuestionCount((current) => Math.min(activeLayout.max_question_count, Number(current || 100)));
                      setPerceptionCount((current) => Math.min(Number(current || 0), activeLayout.max_perception_count));
                    }
                  }}>
                    <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hybrid">Cartão + aplicação online</SelectItem>
                      <SelectItem value="online_only">Exclusivamente online</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {onlineOnly ? (
                  <div className="border border-primary bg-primary/5 p-3 text-sm" data-testid="online-only-exam-notice">
                    <b>Aplicação exclusivamente online</b>
                    <div className="text-muted-foreground mt-1">Permite até 200 questões. Cartão, envio de imagens e leitura OMR ficam desativados.</div>
                  </div>
                ) : <div>
                  <div className="label-eyebrow mb-1">layout do cartão</div>
                  <Select value={layoutId} onValueChange={(value) => {
                    const next = layouts.find((item) => item.id === value);
                    setLayoutId(value);
                    setComposition(null);
                    if (next) {
                      setPerceptionCount((current) => Math.min(Number(current || 0), next.max_perception_count));
                      setSelected((current) => current.slice(0, next.max_question_count));
                      setQuestionCount(Number(next.default_question_count || next.max_question_count));
                    }
                  }}>
                    <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>{layouts.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="text-xs text-muted-foreground mt-1">Capacidade: {activeLayout.max_question_count} questões</div>
                </div>}
                <div className="border border-primary bg-primary/5 p-3 space-y-3" data-testid="exam-smart-composition">
                  <div>
                    <div className="label-eyebrow mb-1">montagem inteligente</div>
                    <Select value={styleId} onValueChange={(value) => {
                      setStyleId(value); setComposition(null);
                      const style = examStyles.find((item) => item.id === value);
                      if (style && !style.builtin) {
                        setQuestionCount(Math.min(effectiveQuestionMax, Number(style.default_question_count || 100)));
                      }
                      if (style?.perception_count && !onlineOnly) setPerceptionCount(style.perception_count);
                    }}>
                      <SelectTrigger className="rounded-none bg-card"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Manual · escolher uma a uma</SelectItem>
                        {examStyles.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="outline" size="sm" className="rounded-none mt-2 w-full" onClick={() => setStyleEditorOpen(true)} disabled={!taxonomy} data-testid="exam-builder-create-style">
                      <Plus className="mr-2" />Criar novo modelo de prova
                    </Button>
                  </div>
                  {styleId !== "manual" && <>
                    <div>
                      <div className="label-eyebrow mb-1">número de questões</div>
                      <Input type="number" min={1} max={effectiveQuestionMax} className="rounded-none bg-card" value={questionCount} onChange={(event) => { setQuestionCount(event.target.value); setComposition(null); }} />
                    </div>
                    {activeStyle && <div className="text-xs text-muted-foreground leading-relaxed">
                      {activeStyle.description}
                      <div className="mt-2 space-y-1">{(activeStyle.groups || []).map((group) => <div key={group.id} className="flex justify-between gap-3"><span>{group.name}</span><b>{group.target}</b></div>)}</div>
                      <div className="mt-2 italic">{activeStyle.source_note}</div>
                    </div>}
                    <div>
                      <div className="label-eyebrow mb-1">dificuldade esperada</div>
                      <Select value={compositionFilters.expected_difficulty} onValueChange={(value) => { setCompositionFilters({ ...compositionFilters, expected_difficulty: value }); setComposition(null); }}>
                        <SelectTrigger className="rounded-none bg-card"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="all">Qualquer</SelectItem>{(taxonomy?.expected_difficulties || []).map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><div className="label-eyebrow mb-1">acerto mín. %</div><Input type="number" min={0} max={100} placeholder="0" className="rounded-none bg-card" value={compositionFilters.observed_correct_min} onChange={(event) => { setCompositionFilters({ ...compositionFilters, observed_correct_min: event.target.value }); setComposition(null); }} /></div>
                      <div><div className="label-eyebrow mb-1">acerto máx. %</div><Input type="number" min={0} max={100} placeholder="100" className="rounded-none bg-card" value={compositionFilters.observed_correct_max} onChange={(event) => { setCompositionFilters({ ...compositionFilters, observed_correct_max: event.target.value }); setComposition(null); }} /></div>
                    </div>
                    <div>
                      <div className="label-eyebrow mb-1">discriminação mínima</div>
                      <Select value={compositionFilters.min_discrimination || "none"} onValueChange={(value) => { setCompositionFilters({ ...compositionFilters, min_discrimination: value === "none" ? "" : value }); setComposition(null); }}>
                        <SelectTrigger className="rounded-none bg-card"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="none">Qualquer</SelectItem><SelectItem value="0.10">0,10 · baixa ou maior</SelectItem><SelectItem value="0.20">0,20 · aceitável ou maior</SelectItem><SelectItem value="0.30">0,30 · boa ou maior</SelectItem><SelectItem value="0.40">0,40 · excelente</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <label className="flex gap-2 text-xs"><input type="checkbox" checked={compositionFilters.include_unmeasured} onChange={(event) => { setCompositionFilters({ ...compositionFilters, include_unmeasured: event.target.checked }); setComposition(null); }} /><span>Incluir questões novas, ainda sem psicometria observada</span></label>
                    <Button type="button" className="rounded-none w-full" onClick={composeSelection} disabled={composing}>
                      {composing && <Spinner className="mr-2 animate-spin" />}Sortear questões
                    </Button>
                    {composition && <div className={`border p-2 text-xs ${composition.complete ? "border-[hsl(var(--success))]" : "border-destructive"}`}>
                      <b>{composition.selected_count}/{composition.requested} selecionadas</b> · {composition.available_after_filters} disponível(is) após os filtros.
                      <div className="mt-2 space-y-1">{(composition.allocation || []).map((item) => <div key={item.id} className="flex justify-between gap-3"><span>{item.name}</span><span>{item.selected}/{item.target}{item.missing ? ` · faltam ${item.missing}` : ""}</span></div>)}</div>
                    </div>}
                  </>}
                </div>
                {!onlineOnly && <div>
                  <div className="label-eyebrow mb-1">questões de percepção</div>
                  <Input type="number" min={0} max={activeLayout.max_perception_count} disabled={activeLayout.max_perception_count === 0} className="rounded-none" value={perceptionCount} onChange={(e) => setPerceptionCount(e.target.value)} />
                  {styleId === "enamed-2025-2026" && <div className="text-xs text-primary mt-1">As 9 perguntas oficiais de percepção do ENAMED serão copiadas automaticamente.</div>}
                  {activeLayout.max_perception_count === 0 && <div className="text-xs text-muted-foreground mt-1">Este cartão não possui campos físicos de percepção.</div>}
                </div>}
                <label className="flex gap-3 border border-primary bg-primary/5 p-3 text-sm">
                  <input type="checkbox" checked={randomize} onChange={(e) => setRandomize(e.target.checked)} />
                  <span><b>Randomizar alternativas</b><br /><span className="text-muted-foreground">Distribui as corretas entre A–D e grava a versão aplicada.</span></span>
                </label>
                <Button type="button" className="rounded-none w-full" onClick={() => { setEditing(null); setEditorOpen(true); }}>
                  <Plus className="mr-2" />Criar nova questão
                </Button>
                <Button type="button" className="rounded-none w-full h-12 font-mono-data uppercase tracking-widest" onClick={create} disabled={saving || selected.length === 0}>
                  {saving && <Spinner className="mr-2 animate-spin" />}Criar prova com {selected.length} questão(ões)
                </Button>
              </section>
            </aside>

            <main className="space-y-5 min-w-0">
              <section className="border border-primary bg-primary/5">
                <div className="px-4 py-3 border-b border-primary/30 flex justify-between items-center">
                  <div><b className="font-mono-data">Questões da prova</b><span className="text-xs text-muted-foreground ml-2">{selected.length} selecionada(s)</span></div>
                  {selected.length > 0 && <button type="button" className="text-xs underline" onClick={() => { setSelected([]); setComposition(null); }}>Limpar</button>}
                </div>
                {selected.length === 0 ? (
                  <div className="p-6 text-sm text-muted-foreground">Use a busca abaixo para adicionar questões. Você também pode criar uma nova questão agora.</div>
                ) : (
                  <>
                    <div className="p-3 border-b border-primary/20 bg-card" data-testid="selected-editorial-summary">
                      <div className="label-eyebrow mb-2">Autor editorial / banca na prova</div>
                      <div className="text-xs text-muted-foreground mb-2">O crédito será impresso questão a questão; a autoria do próprio professor responsável pela prova é omitida.</div>
                      <div className="flex flex-wrap gap-2">
                        {selectedEditorialSummary.map(([author, count]) => (
                          <span key={author} className="border border-border bg-background px-2 py-1 text-xs"><b>{author}</b> · {count}</span>
                        ))}
                      </div>
                    </div>
                    <div className="divide-y divide-primary/20">
                      {selected.map((question, index) => (
                        <div key={question.id} className="p-3 flex items-center gap-3 bg-card/70">
                        <b className="font-display text-xl w-8">{index + 1}</b>
                        <div className="min-w-0 flex-1">
                          <div className="font-mono-data text-xs text-primary">{question.code}</div>
                          <div className="truncate text-sm">{question.stem}</div>
                          <div className="truncate text-xs text-muted-foreground mt-1"><b>Autor editorial / banca:</b> {editorialAuthor(question)}</div>
                        </div>
                        <Button type="button" size="icon" variant="outline" className="rounded-none" disabled={index === 0} onClick={() => move(index, -1)}><ArrowUp /></Button>
                        <Button type="button" size="icon" variant="outline" className="rounded-none" disabled={index === selected.length - 1} onClick={() => move(index, 1)}><ArrowDown /></Button>
                        {question.can_edit && <Button type="button" size="icon" variant="outline" className="rounded-none" onClick={() => { setEditing(question); setEditorOpen(true); }}><PencilSimple /></Button>}
                        <Button type="button" size="icon" variant="ghost" className="rounded-none text-destructive" onClick={() => toggle(question)}><Trash /></Button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </section>

              <section className="border border-border bg-card p-4 space-y-3">
                <div className="flex flex-wrap gap-2">
                  {[
                    ["all", "Todas acessíveis"], ["mine", "Minhas"], ["public", "Públicas"],
                    ...(taxonomy?.has_hierarchy_questions ? [["hierarchy", "Minha hierarquia"]] : []),
                  ].map(([value, label]) => (
                    <Button type="button" key={value} size="sm" variant={filters.scope === value ? "default" : "outline"} className="rounded-none" onClick={() => setFilters({ ...filters, scope: value })}>{label}</Button>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="relative"><MagnifyingGlass className="absolute left-3 top-3 text-muted-foreground" /><Input className="rounded-none pl-10" placeholder="Buscar questão, código, lei ou marcador" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} /></div>
                  <Select value={filters.knowledge_area} onValueChange={(value) => setFilters({ ...filters, knowledge_area: value })}>
                    <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Todas as áreas</SelectItem>{(taxonomy?.knowledge_areas || []).map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={filters.system_context} onValueChange={(value) => setFilters({ ...filters, system_context: value })}>
                    <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Todos os sistemas/contextos</SelectItem>{(taxonomy?.system_contexts || []).map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </section>

              <section className="space-y-3">
                {loading ? <div className="p-16 text-center border border-border"><Spinner className="animate-spin mx-auto" size={32} /></div> : questions.length === 0 ? (
                  <div className="p-12 text-center border border-border bg-card"><div className="font-display text-2xl font-black">Nenhuma questão encontrada</div><Button type="button" className="rounded-none mt-4" onClick={() => { setEditing(null); setEditorOpen(true); }}><Plus className="mr-2" />Criar questão</Button></div>
                ) : questions.map((question) => (
                  <QuestionRow
                    key={question.id}
                    question={question}
                    selected={selectedIds.has(question.id)}
                    onToggle={toggle}
                    onEdit={(item) => { setEditing(item); setEditorOpen(true); }}
                    onDuplicate={duplicate}
                  />
                ))}
              </section>
            </main>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Cancelar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ExamStyleEditorDialog
        open={styleEditorOpen}
        onOpenChange={setStyleEditorOpen}
        taxonomy={taxonomy}
        defaultSchoolId={selectedSchoolId}
        onSaved={(saved) => {
          setExamStyles((current) => [...current.filter((item) => item.id !== saved.id), saved]);
          setStyleId(saved.id);
          setQuestionCount(Math.min(activeLayout.max_question_count, Number(saved.default_question_count || 100)));
          if (saved.perception_count) setPerceptionCount(saved.perception_count);
          setComposition(null);
        }}
      />

      <QuestionEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        question={editing}
        taxonomy={taxonomy}
        onSaved={handleSaved}
      />
    </>
  );
}
